import type {
  ShiftAssignment,
  Shift,
  ShiftAssignmentShiftRef,
} from '@/types/shift.types'

export function getAssignmentUserId(assignment: ShiftAssignment): string {
  return typeof assignment.userId === 'string'
    ? assignment.userId
    : assignment.userId._id
}

export function getAssignmentShift(
  assignment: ShiftAssignment,
  shiftById: Map<string, Shift>
): Shift | ShiftAssignmentShiftRef | undefined {
  if (assignment.startTime || assignment.endTime || assignment.slotName) {
    return {
      _id:
        assignment.templateSnapshot?.shiftId ||
        (typeof assignment.shiftId === 'string'
          ? assignment.shiftId
          : assignment.shiftId?._id) ||
        assignment._id,
      name: assignment.slotName ?? assignment.templateSnapshot?.name ?? 'Custom shift',
      code: assignment.templateSnapshot?.code,
      startTime: assignment.startTime ?? '',
      endTime: assignment.endTime ?? '',
      color: assignment.color,
      breakAllowances: assignment.breakAllowances,
    }
  }
  if (assignment.shift) return assignment.shift
  const ref = assignment.shiftId
  if (!ref) return undefined
  if (typeof ref === 'object' && ref && 'name' in ref) return ref
  const id = typeof ref === 'string' ? ref : (ref as ShiftAssignmentShiftRef)._id
  return shiftById.get(id)
}

export function getAssignmentShiftName(assignment: ShiftAssignment): string {
  if (assignment.slotName) return assignment.slotName
  if (assignment.templateSnapshot?.name) return assignment.templateSnapshot.name
  if (assignment.shift?.name) return assignment.shift.name
  const ref = assignment.shiftId
  if (typeof ref === 'object' && ref && 'name' in ref) return ref.name
  return assignment.shiftSourceType === 'custom' ? 'Custom shift' : 'Shift'
}

function getAssignmentShiftCode(assignment: ShiftAssignment): string | undefined {
  if (assignment.templateSnapshot?.code) return assignment.templateSnapshot.code
  if (assignment.shift?.code) return assignment.shift.code
  const ref = assignment.shiftId
  if (typeof ref === 'object' && ref && 'code' in ref) return ref.code
  return undefined
}

/** Builds full tooltip text: name (code), time range, status, notes. */
export function getAssignmentTooltipText(assignment: ShiftAssignment): string {
  const name = getAssignmentShiftName(assignment)
  const code = getAssignmentShiftCode(assignment)
  const shift =
    getAssignmentShift(assignment, new Map()) ??
    assignment.shift ??
    (typeof assignment.shiftId === 'object' && assignment.shiftId && 'startTime' in assignment.shiftId ? assignment.shiftId : null)
  const startTime = shift && 'startTime' in shift ? shift.startTime : ''
  const endTime = shift && 'endTime' in shift ? shift.endTime : ''
  const timeRange = startTime && endTime ? `${startTime}–${endTime}` : ''
  const parts: string[] = [code ? `${name} (${code})` : name]
  if (timeRange) parts.push(timeRange)
  if (assignment.shiftSourceType === 'custom') parts.push('custom')
  parts.push(assignment.status)
  if (assignment.notes) parts.push(assignment.notes)
  return parts.join(' · ')
}
